import { MAX_CREDENTIAL_HOSTS, MAX_SENDER_DOMAINS } from "@iqa/contracts/credential-rules";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Credential, CredentialUse, Device, PortalApi } from "../api.ts";
import { Id, shortId } from "../Id.tsx";
import { LocalTime, TimeZoneNote } from "../LocalTime.tsx";
import { type Loaded, messageOf, useLoad } from "../load.ts";
import {
  CODE_KINDS,
  type CodeKind,
  codeText,
  type DraftProblems,
  draftOf,
  draftProblems,
  emptyDraft,
  FORM_FIELDS,
  type FormKey,
  type LoginDraft,
  newLoginId,
  saveArgsOf,
} from "../logins.ts";
import {
  Badge,
  Button,
  ConfirmButton,
  facts,
  Input,
  Notice,
  PageLayout,
  Select,
  Table,
} from "../ui.tsx";
import { MailSection } from "./Mail.tsx";

/**
 * Saved logins (docs/credentials.md#the-admin-portal), for every role: the list, a login with its
 * uses, the form to add or change one, and the Mail section (the forwarding of emailed codes). The
 * password is write-only: typed here, sent once, never shown or prefilled. Routes: `#/logins`,
 * `#/logins/new`, `#/logins/<id>`, `#/logins/mail`.
 */

/** A notice kept for one route, so one set before moving to a page shows there. */
interface PageNotice {
  readonly route: string;
  readonly text: string;
  readonly error?: boolean;
}

export function LoginsPage({
  api,
  path,
  signInMail,
}: {
  api: PortalApi;
  path: readonly string[];
  /** The forwarding address of this environment (`config.json`); none where it has none. */
  signInMail?: string | undefined;
}) {
  const list = useLoad(() => api.credentials(), [api]);
  const [notice, setNotice] = useState<PageNotice>();
  const [first] = path;
  const route = first ?? "";
  const tell = (text: string, error?: boolean, at: string = route) =>
    setNotice({ route: at, text, ...(error && { error }) });
  const shown = notice?.route === route ? notice : undefined;

  /** Puts a changed login in the list in place of its old one, by label as the API sorts. */
  const replace = (login: Credential) => {
    const others = (list.data ?? []).filter((other) => other.id !== login.id);
    list.set([...others, login].sort((a, b) => a.label.localeCompare(b.label)));
  };
  const remove = (id: string) => list.set((list.data ?? []).filter((login) => login.id !== id));
  const actions: LoginActions = {
    setEnabled: async (login, enabled) => {
      try {
        replace(await api.setCredentialEnabled(login.id, enabled));
        tell(
          enabled
            ? `Enabled "${login.label}": the daemon signs in with it again.`
            : `Disabled "${login.label}": the daemon no longer signs in with it.`,
        );
      } catch (error) {
        tell(messageOf(error), true);
      }
    },
    delete: async (login) => {
      try {
        await api.deleteCredential(login.id);
        remove(login.id);
        tell(`Deleted "${login.label}".`, false, "");
        if (route !== "") window.location.hash = "#/logins";
      } catch (error) {
        tell(messageOf(error), true);
      }
    },
  };

  const notices = shown && (
    <Notice tone={shown.error ? "bad" : "ok"} role="status">
      {shown.text}
    </Notice>
  );

  if (first === "mail") {
    return (
      <PageLayout title="Logins">
        <Sections current="mail" />
        <MailSection api={api} address={signInMail} logins={list.data ?? []} />
      </PageLayout>
    );
  }
  if (first === "new") {
    return (
      <PageLayout title="Add a login" back={{ href: "#/logins", label: "← Logins" }}>
        <LoginForm
          api={api}
          onSaved={(login) => {
            replace(login);
            tell(`Saved "${login.label}". Its password is not shown again.`, false, login.id);
            window.location.hash = `#/logins/${encodeURIComponent(login.id)}`;
          }}
        />
      </PageLayout>
    );
  }
  if (first !== undefined && first !== "") {
    return (
      <LoginPage
        api={api}
        id={first}
        list={list}
        notices={notices}
        actions={actions}
        onSaved={(login) => {
          replace(login);
          tell(`Saved "${login.label}".`);
        }}
        onNothing={() => tell("Nothing changed.")}
      />
    );
  }
  return (
    <PageLayout title="Logins">
      <Sections current="logins" />
      <p className="max-w-[80ch]">
        A saved login is a user ID and password for a system the agency signs in to. When a run's
        page asks for a sign-in on one of its hosts, the daemon signs in by itself; the model never
        sees the password. A password typed here is never shown again, to anyone.
      </p>
      <p className="my-4">
        <a
          href="#/logins/new"
          className="inline-flex h-[30px] items-center rounded-md bg-accent-solid px-3 font-medium text-on-accent text-ui hover:bg-accent-solid-hover hover:no-underline"
        >
          Add a login
        </a>
      </p>
      {notices}
      {list.loading && <p className="text-muted">Loading…</p>}
      {list.error && <Notice tone="bad">{list.error}</Notice>}
      {list.data && list.data.length === 0 && <p className="text-muted">No saved logins yet.</p>}
      {list.data && list.data.length > 0 && (
        <>
          <LoginTable logins={list.data} actions={actions} />
          <TimeZoneNote />
        </>
      )}
    </PageLayout>
  );
}

interface LoginActions {
  setEnabled(login: Credential, enabled: boolean): Promise<void>;
  delete(login: Credential): Promise<void>;
}

/** The two sections of the page: the logins, and the mail their emailed codes come in. */
function Sections({ current }: { current: "logins" | "mail" }) {
  const tab = (id: "logins" | "mail", href: string, label: string) => (
    <a
      href={href}
      aria-current={current === id ? "page" : undefined}
      className="-mb-px border-transparent border-b-2 px-1 pb-1.5 font-medium text-fg-2 hover:text-fg hover:no-underline aria-[current=page]:border-fg aria-[current=page]:text-fg"
    >
      {label}
    </a>
  );
  return (
    <nav aria-label="Logins sections" className="mt-4 mb-5 flex gap-4 border-line border-b text-ui">
      {tab("logins", "#/logins", "Saved logins")}
      {tab("mail", "#/logins/mail", "Mail")}
    </nav>
  );
}

/** Who did something: their email where it is known, else their id. */
function Who({ email, id }: { email?: string | undefined; id?: string | undefined }) {
  if (email) return <>{email}</>;
  return id ? <span title={id}>{shortId(id)}</span> : <>—</>;
}

function LastUse({ login }: { login: Credential }) {
  if (!login.lastUsedAt) return <span className="text-muted">never</span>;
  return (
    <>
      <LocalTime iso={login.lastUsedAt} />
      {(login.lastUsedByEmail || login.lastUsedBy) && (
        <span className="text-muted">
          {" "}
          by <Who email={login.lastUsedByEmail} id={login.lastUsedBy} />
        </span>
      )}
    </>
  );
}

function LastFailure({ login }: { login: Credential }) {
  const failure = login.lastFailure;
  if (!failure) return <span className="text-muted">—</span>;
  return (
    <span className="text-bad">
      <LocalTime iso={failure.at} /> {failure.outcome}
      {failure.host && <> on {failure.host}</>}
    </span>
  );
}

function Lines({ values }: { values: readonly string[] }) {
  if (values.length === 0) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-col">
      {values.map((value) => (
        <code key={value}>{value}</code>
      ))}
    </span>
  );
}

function LoginTable({ logins, actions }: { logins: readonly Credential[]; actions: LoginActions }) {
  return (
    <Table>
      <thead>
        <tr>
          <th>Login</th>
          <th>System</th>
          <th>Hosts</th>
          <th>User ID</th>
          <th>Code</th>
          <th>Sender domains</th>
          <th>Enabled</th>
          <th>Last use</th>
          <th>Last failure</th>
          <th>Change</th>
        </tr>
      </thead>
      <tbody>
        {logins.map((login) => (
          <tr key={login.id}>
            <td>
              <a href={`#/logins/${encodeURIComponent(login.id)}`}>{login.label}</a>
            </td>
            <td>{login.system}</td>
            <td>
              <Lines values={login.hosts} />
            </td>
            <td>{login.username}</td>
            <td>{codeText(login.code)}</td>
            <td>
              {login.code === "email" ? (
                login.senderDomains.length > 0 ? (
                  <Lines values={login.senderDomains} />
                ) : (
                  <span className="text-muted">learned from the first code</span>
                )
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td>
              <Badge tone={login.enabled ? "ok" : "neutral"}>{login.enabled ? "yes" : "no"}</Badge>
            </td>
            <td>
              <LastUse login={login} />
            </td>
            <td>
              <LastFailure login={login} />
            </td>
            <td className="space-x-1.5">
              <LoginButtons login={login} actions={actions} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function LoginButtons({ login, actions }: { login: Credential; actions: LoginActions }) {
  const [busy, setBusy] = useState(false);
  const act = async (change: () => Promise<void>) => {
    setBusy(true);
    await change();
    setBusy(false);
  };
  return (
    <>
      <Button
        type="button"
        size="sm"
        disabled={busy}
        aria-label={`${login.enabled ? "Disable" : "Enable"} ${login.label}`}
        onClick={() => act(() => actions.setEnabled(login, !login.enabled))}
      >
        {login.enabled ? "Disable" : "Enable"}
      </Button>
      <ConfirmButton
        label="Delete"
        question={`Delete "${login.label}"?`}
        confirm="Yes, delete it"
        disabled={busy}
        onConfirm={() => act(() => actions.delete(login))}
      />
    </>
  );
}

/** A login: what it is, the form to change it, and its last uses. */
function LoginPage({
  api,
  id,
  list,
  notices,
  actions,
  onSaved,
  onNothing,
}: {
  api: PortalApi;
  id: string;
  list: Loaded<Credential[]>;
  notices: ReactNode;
  actions: LoginActions;
  onSaved: (login: Credential) => void;
  onNothing: () => void;
}) {
  const login = list.data?.find((candidate) => candidate.id === id);
  const back = { href: "#/logins", label: "← Logins" };
  if (!login) {
    return (
      <PageLayout title="Login" back={back}>
        {notices}
        {list.loading && <p className="text-muted">Loading…</p>}
        {list.error && <Notice tone="bad">{list.error}</Notice>}
        {list.data && <Notice tone="bad">There is no saved login {id}.</Notice>}
      </PageLayout>
    );
  }
  return (
    <PageLayout title={login.label} back={back}>
      {notices}
      <dl className={facts}>
        <dt>Enabled</dt>
        <dd>
          <Badge tone={login.enabled ? "ok" : "neutral"}>{login.enabled ? "yes" : "no"}</Badge>{" "}
          <span className="ml-1.5 inline-flex gap-1.5">
            <LoginButtons login={login} actions={actions} />
          </span>
        </dd>
        <dt>Last use</dt>
        <dd>
          <LastUse login={login} />
        </dd>
        <dt>Last failure</dt>
        <dd>
          <LastFailure login={login} />
        </dd>
        <dt>Password set</dt>
        <dd>
          <LocalTime iso={login.secretChangedAt} />
        </dd>
        <dt>Changed</dt>
        <dd>
          <LocalTime iso={login.updatedAt} />
        </dd>
      </dl>
      <h2>Change</h2>
      {/* A saved change starts the form again from the login as the API answered it. */}
      <LoginForm
        key={`${login.id} ${login.updatedAt}`}
        api={api}
        saved={login}
        onSaved={onSaved}
        onNothing={onNothing}
      />
      <Uses api={api} login={login} />
    </PageLayout>
  );
}

const STEPS: Record<CredentialUse["step"], string> = { secret: "password", code: "emailed code" };

const OUTCOMES: Record<CredentialUse["outcome"], { text: string; tone: "ok" | "bad" }> = {
  sent: { text: "sent", tone: "ok" },
  deviceRefused: { text: "device refused", tone: "bad" },
  disabled: { text: "login disabled", tone: "bad" },
  hostRefused: { text: "not its host", tone: "bad" },
  codeRefused: { text: "takes no emailed code", tone: "bad" },
  failed: { text: "failed", tone: "bad" },
};

/** A login's last 50 fetches: who, on which device, for which host, and what became of it. */
function Uses({ api, login }: { api: PortalApi; login: Credential }) {
  const uses = useLoad(() => api.credentialUses(login.id), [api, login.id]);
  // Devices name a use's device; a member sees only their own, the others by id.
  const devices = useLoad(() => api.devices().catch((): Device[] => []), [api]);
  const names = new Map((devices.data ?? []).map((device) => [device.id, device.name]));
  return (
    <>
      <h2>Uses</h2>
      <p className="text-muted">
        The last 50 times a device fetched its password or an emailed code.
      </p>
      {uses.loading && <p className="text-muted">Loading…</p>}
      {uses.error && <Notice tone="bad">{uses.error}</Notice>}
      {uses.data && uses.data.length === 0 && <p className="text-muted">Not used yet.</p>}
      {uses.data && uses.data.length > 0 && (
        <Table compact>
          <thead>
            <tr>
              <th>When</th>
              <th>Producer</th>
              <th>Device</th>
              <th>Host</th>
              <th>Step</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {uses.data.map((use) => {
              const outcome = OUTCOMES[use.outcome] ?? { text: use.outcome, tone: "bad" as const };
              return (
                <tr key={`${use.at} ${use.runId} ${use.step}`}>
                  <td>
                    <LocalTime iso={use.at} />
                  </td>
                  <td>
                    <Who email={use.email} id={use.producerId} />
                  </td>
                  <td>{names.get(use.deviceId) ?? <Id id={use.deviceId} />}</td>
                  <td>{use.host ?? <span className="text-muted">—</span>}</td>
                  <td>{STEPS[use.step] ?? use.step}</td>
                  <td>
                    <Badge tone={outcome.tone}>{outcome.text}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}

/** A labelled part of the form: its label, the control, a hint and the problem with it. */
function Row({
  label,
  htmlFor,
  hint,
  problem,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  problem?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1 md:grid-cols-[11rem_minmax(0,36rem)] md:gap-x-4">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="pt-1.5 font-medium text-fg-2 text-ui">
          {label}
        </label>
      ) : (
        <span className="pt-1.5 font-medium text-fg-2 text-ui">{label}</span>
      )}
      <div className="grid gap-1">
        {children}
        {problem && <span className="text-bad text-xs">{problem}</span>}
        {hint && <span className="text-muted text-xs">{hint}</span>}
      </div>
    </div>
  );
}

/** Rows of one text each (hosts, sender domains), with Add and Remove. */
function ListEditor({
  name,
  what,
  values,
  max,
  problems,
  placeholder,
  onChange,
}: {
  name: string;
  what: string;
  values: readonly string[];
  max: number;
  problems: DraftProblems;
  placeholder: string;
  onChange: (values: string[]) => void;
}) {
  return (
    <div className="grid gap-1.5">
      {values.map((value, index) => {
        const problem = problems[`${name}.${index}`];
        return (
          // Rows are edited in place, by position; each input is controlled, so a key by index holds.
          <div key={index} className="grid gap-0.5">
            <span className="flex items-center gap-1.5">
              <Input
                aria-label={`${what} ${index + 1}`}
                aria-invalid={problem ? true : undefined}
                value={value}
                placeholder={placeholder}
                spellCheck={false}
                autoCapitalize="none"
                className="w-full max-w-[24rem]"
                onChange={(event) =>
                  onChange(values.map((row, at) => (at === index ? event.target.value : row)))
                }
              />
              <Button
                type="button"
                size="sm"
                variant="quiet"
                aria-label={`Remove ${what.toLowerCase()} ${index + 1}`}
                onClick={() => onChange(values.filter((_, at) => at !== index))}
              >
                Remove
              </Button>
            </span>
            {problem && <span className="text-bad text-xs">{problem}</span>}
          </div>
        );
      })}
      <span>
        <Button
          type="button"
          size="sm"
          disabled={values.length >= max}
          onClick={() => onChange([...values, ""])}
        >
          Add a {what.toLowerCase()}
        </Button>
      </span>
    </div>
  );
}

/**
 * The form to add a login (no `saved`) or change one. The password field is never filled from a
 * login (no answer holds one) and is emptied as soon as the form is sent; on a change, left empty it
 * keeps the saved password. The rules the data API checks are checked here first; anything else it
 * refuses is shown as it says.
 */
function LoginForm({
  api,
  saved,
  onSaved,
  onNothing,
}: {
  api: PortalApi;
  saved?: Credential;
  onSaved: (login: Credential) => void;
  onNothing?: () => void;
}) {
  const [draft, setDraft] = useState<LoginDraft>(() => (saved ? draftOf(saved) : emptyDraft()));
  const [newId] = useState(newLoginId);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const creating = saved === undefined;
  const problems = checked ? draftProblems(draft, creating) : {};
  const set = <K extends keyof LoginDraft>(name: K, value: LoginDraft[K]) =>
    setDraft((current) => ({ ...current, [name]: value }));
  const setSelector = (key: FormKey, value: string) =>
    setDraft((current) => ({ ...current, form: { ...current.form, [key]: value } }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setChecked(true);
    setError(undefined);
    if (Object.keys(draftProblems(draft, creating)).length > 0) return;
    const args = saveArgsOf(draft, saved, newId);
    // The password leaves the form's state as it is sent, whatever the answer.
    setDraft((current) => ({ ...current, password: "" }));
    if (!args) {
      onNothing?.();
      return;
    }
    setBusy(true);
    try {
      onSaved(await api.saveCredential(args));
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy(false);
    }
  };

  const ids = (name: string) => `login-${name}`;
  const emailed = draft.code === "email";
  // Advanced starts open where the login has any of it; then it is the producer's to open or close.
  const [advanced] = useState(
    () => draft.codePattern !== "" || FORM_FIELDS.some(({ key }) => draft.form[key] !== ""),
  );
  return (
    <form
      onSubmit={submit}
      aria-label={creating ? "Add a login" : "Change the login"}
      noValidate
      className="my-4 grid gap-3"
    >
      <Row
        label="Label"
        htmlFor={ids("label")}
        problem={problems.label}
        hint='Such as "Monica — National General"'
      >
        <Input
          id={ids("label")}
          value={draft.label}
          onChange={(event) => set("label", event.target.value)}
          className="w-full max-w-[24rem]"
        />
      </Row>
      <Row
        label="System"
        htmlFor={ids("system")}
        problem={problems.system}
        hint="The carrier's key (national-general), or the system's name (Arive)"
      >
        <Input
          id={ids("system")}
          value={draft.system}
          onChange={(event) => set("system", event.target.value)}
          className="w-full max-w-[24rem]"
        />
      </Row>
      <Row
        label="Hosts"
        problem={problems.hosts}
        hint="Where it is filled, over https only: an exact host such as portal.steadily.com, or *.arive.com for every host under arive.com (not arive.com itself)"
      >
        <ListEditor
          name="hosts"
          what="Host"
          values={draft.hosts}
          max={MAX_CREDENTIAL_HOSTS}
          problems={problems}
          placeholder="portal.example.com"
          onChange={(hosts) => set("hosts", hosts)}
        />
      </Row>
      <Row label="User ID" htmlFor={ids("username")} problem={problems.username}>
        <Input
          id={ids("username")}
          value={draft.username}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => set("username", event.target.value)}
          className="w-full max-w-[24rem]"
        />
      </Row>
      <Row
        label={creating ? "Password" : "New password"}
        htmlFor={ids("password")}
        problem={problems.password}
        hint={
          creating
            ? "Never shown again, to anyone."
            : "Leave it empty to keep the saved password; it is never shown."
        }
      >
        <Input
          id={ids("password")}
          type="password"
          autoComplete="new-password"
          value={draft.password}
          onChange={(event) => set("password", event.target.value)}
          className="w-full max-w-[24rem]"
        />
      </Row>
      <Row
        label="Code"
        htmlFor={ids("code")}
        hint={
          draft.code === "text"
            ? "The producer types a texted code when the page asks for it."
            : emailed
              ? `The agency's Gmail forwards the system's code mail to us (Mail).${
                  // A page with no password field is found only by its user ID field (#161).
                  draft.form.username.trim() === ""
                    ? " If its sign-in asks for no password (Steadily: an email, then a code), set Advanced → User ID field too, or the page is not recognised."
                    : ""
                }`
              : undefined
        }
      >
        <Select
          id={ids("code")}
          value={draft.code}
          onChange={(event) => set("code", event.target.value as CodeKind)}
          className="w-fit"
        >
          {CODE_KINDS.map((kind) => (
            <option key={kind.value} value={kind.value}>
              {kind.label}
            </option>
          ))}
        </Select>
      </Row>
      {emailed && (
        <Row
          label="Sender domains"
          problem={problems.senderDomains}
          hint="The mail domains its codes come from, such as steadily.com (a sub-domain matches too). Left empty, the first code's sender is learned."
        >
          <ListEditor
            name="senderDomains"
            what="Sender domain"
            values={draft.senderDomains}
            max={MAX_SENDER_DOMAINS}
            problems={problems}
            placeholder="example.com"
            onChange={(domains) => set("senderDomains", domains)}
          />
        </Row>
      )}
      <details open={advanced}>
        <summary>Advanced</summary>
        <div className="mt-2 grid gap-3">
          <p className="m-0 text-muted text-xs">
            Only for a page the daemon cannot read by itself: CSS selectors of its parts.
          </p>
          {FORM_FIELDS.map(({ key, label }) => (
            <Row
              key={key}
              label={label}
              htmlFor={ids(`form-${key}`)}
              problem={problems[`form.${key}`]}
            >
              <Input
                id={ids(`form-${key}`)}
                value={draft.form[key]}
                spellCheck={false}
                placeholder="#selector"
                onChange={(event) => setSelector(key, event.target.value)}
                className="w-full max-w-[24rem] font-mono"
              />
            </Row>
          ))}
          {emailed && (
            <Row
              label="Code pattern"
              htmlFor={ids("code-pattern")}
              problem={problems.codePattern}
              hint="Only where the usual reading of its code mail fails: a regular expression whose first group (else the match) is the code. Empty: the usual reading."
            >
              <Input
                id={ids("code-pattern")}
                value={draft.codePattern}
                spellCheck={false}
                onChange={(event) => set("codePattern", event.target.value)}
                className="w-full max-w-[24rem] font-mono"
              />
            </Row>
          )}
        </div>
      </details>
      {error && <Notice tone="bad">{error}</Notice>}
      <div className="md:ml-[12rem]">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Saving…" : creating ? "Add the login" : "Save the change"}
        </Button>
      </div>
    </form>
  );
}
